import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { trackContentSave } from '../../lib/gaEvents';
import { Alert, Button, Typography, Box, Stack, Divider } from '@mui/material';
import ProgressBar from '../../components/common/ProgressBar';
import CommentSection from '../../components/comment/CommentSection';
import { noticesApi } from '../../api/notices';
import { commentApi } from '../../api/comment';
import { useUserStore } from '../../stores/userStore';
import EditDeleteMenuButton from '../../components/button/EditDeleteMenuButton';
import CommentCount from '../../components/common/CommentCount';
import BookmarkCount from '../../components/common/BookmarkCount';
import PageLayout from '../../components/layout/PageLayout';
import theme from '../../theme';
import BookmarkButton from '../../components/button/BookmarkButton';
import PinButton from '../../components/button/PinButton';
import NoData from '../../components/common/NoData';
import ContentViewer from '../../components/common/ContentViewer';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import InFeedBoxAd from '../../components/ad/InFeedBoxAd';
import SidebarLayout from '../../components/layout/SidebarLayout';
import ShareButton from '../../components/button/ShareButton';
import { SITE_URL } from '../../utils/env';
import { Helmet } from 'react-helmet-async';

const NoticeDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loadedNotice, setNotice] = useState(null);
  const user = useUserStore((state) => state.user);
  const isAdmin = useUserStore((state) => state.isAdmin);
  const notice = loadedNotice?.isHidden && !isAdmin ? null : loadedNotice;
  useTrackPageView(
    {
      pageType: PAGE_TYPE.NOTICE_POST,
      contentType: CONTENT_TYPE.NOTICE,
      contentId: notice?.id,
      contentName: notice?.title,
    },
    { ready: !!notice?.id },
  );
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [visibilityBusy, setVisibilityBusy] = useState(false);
  const [visibilityError, setVisibilityError] = useState('');

  // 댓글 스크롤: path parameter에서 commentId 감지
  const commentViewMatch = location.pathname.match(/\/comment\/(\d+)$/);
  const commentScrollId = commentViewMatch ? parseInt(commentViewMatch[1]) : null;

  useEffect(() => {
    let active = true;
    setNotice(null);
    setComments([]);
    setLoading(true);
    const fetchNoticeDetail = async () => {
      try {
        // 숫자 ID이면 기존 방식, 슬러그이면 by-slug 엔드포인트 사용
        const isNumericId = /^\d+$/.test(slug);
        const noticeRes = isNumericId
          ? await noticesApi.getNoticeById(slug)
          : await noticesApi.getNoticeBySlug(slug);
        const noticeData = noticeRes.data;
        const commentsRes = await commentApi.getComments('notice', noticeData.id);
        if (!active) return;
        setComments(commentsRes.data);
        setNotice(noticeData);
        setIsBookmarked(noticeData.isBookmarked);
        setIsPinned(noticeData.isPinned);
      } catch (err) {
        console.error('Error fetching notice detail:', err);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchNoticeDetail();
    return () => {
      active = false;
    };
  }, [slug, user?.id, isAdmin]);

  const handleBookmarkToggle = async () => {
    try {
      if (isBookmarked) {
        await noticesApi.deleteBookmark(notice.id);
        setNotice((prev) => ({
          ...prev,
          bookmarkCount: prev.bookmarkCount - 1,
        }));
      } else {
        await noticesApi.addBookmark(notice.id);
        setNotice((prev) => ({
          ...prev,
          bookmarkCount: prev.bookmarkCount + 1,
        }));
        trackContentSave(
          {
            contentType: CONTENT_TYPE.NOTICE,
            contentId: notice.id,
            contentName: notice.title,
            pageType: PAGE_TYPE.NOTICE_POST,
          },
          user,
        );
      }
      setIsBookmarked(!isBookmarked);
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
    }
  };

  const handlePinToggle = async () => {
    try {
      await noticesApi.togglePin(notice.id, !isPinned);
      setIsPinned(!isPinned);
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleEditClick = () => {
    navigate(`/notice/${notice?.id}/edit?from=detail`);
  };

  const toggleHidden = async () => {
    if (
      !notice ||
      visibilityBusy ||
      !window.confirm(
        notice.isHidden
          ? '이 공지를 다시 공개할까요?'
          : '이 공지를 숨길까요? 일반 사용자는 목록과 상세 링크에서 볼 수 없지만 댓글과 저장 기록은 유지됩니다.',
      )
    )
      return;
    setVisibilityBusy(true);
    setVisibilityError('');
    try {
      const response = await noticesApi.setHidden(notice.id, !notice.isHidden);
      setNotice((current) =>
        current?.id === notice.id ? { ...current, isHidden: response.data.isHidden } : current,
      );
    } catch (error) {
      setVisibilityError(error.message || '공개 상태를 변경하지 못했습니다.');
    } finally {
      setVisibilityBusy(false);
    }
  };

  const handleDelete = async () => {
    try {
      await noticesApi.deleteNotice(notice.id);
      navigate('/notice');
    } catch (error) {
      console.error('Failed to delete notice:', error);
    }
  };

  const handleCommentSubmit = async (content) => {
    const { data } = await noticesApi.addComment(notice.id, content);
    setNotice((prev) => ({ ...prev, commentCount: prev.commentCount + 1 }));
    return data;
  };

  const handleCommentReply = async (parentId, content, mentionedUserId) => {
    const { data } = await noticesApi.addComment(notice.id, content, parentId, mentionedUserId);
    setNotice((prev) => ({ ...prev, commentCount: prev.commentCount + 1 }));
    return data;
  };

  const handleCommentDelete = () => {
    setNotice((prev) => ({ ...prev, commentCount: prev.commentCount - 1 }));
  };

  return (
    <PageLayout maxWidth={'980px'}>
      {visibilityError && <Alert severity="error">{visibilityError}</Alert>}
      {isAdmin && notice && (
        <Box sx={{ mb: 2 }}>
          {notice.isHidden && (
            <Alert severity="warning">관리자에게만 보이는 숨긴 공지입니다.</Alert>
          )}
          <Button disabled={visibilityBusy} onClick={toggleHidden}>
            {notice.isHidden ? '다시 공개' : '공지 숨기기'}
          </Button>
        </Box>
      )}
      {notice && (
        <>
          <OpenInAppBanner type="notice" title={notice.title} image={notice.imageUrl} />
          <SEO
            title={notice.title}
            description={notice.content.replace(/<[^>]*>?/gm, '').substring(0, 160)} // HTML 태그 제거 후 길이 제한
            image={notice.imageUrl}
            url={
              notice.slug ? `${SITE_URL}/notice/${notice.slug}` : `${SITE_URL}/notice/${notice.id}`
            }
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                  { '@type': 'ListItem', position: 2, name: 'Notice', item: `${SITE_URL}/notice` },
                  { '@type': 'ListItem', position: 3, name: notice.title },
                ],
              })}
            </script>
          </Helmet>
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: notice.title,
                image: notice.imageUrl ? [notice.imageUrl] : undefined,
                datePublished: notice.createdAt?.replace(
                  /(\d+)\.(\d+)\.(\d+)/,
                  (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                ),
                dateModified: notice.updatedAt?.replace(
                  /(\d+)\.(\d+)\.(\d+)/,
                  (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                ),
                author: {
                  '@type': 'Organization',
                  name: 'Postpop',
                  url: SITE_URL,
                },
              })}
            </script>
          </Helmet>
        </>
      )}
      <ProgressBar isLoading={loading}>
        {!notice ? (
          <NoData />
        ) : (
          <SidebarLayout pageType={PAGE_TYPE.NOTICE_POST}>
            {/* 헤더 */}
            <Box
              sx={{
                pb: { xs: '16px', lg: '24px' },
                borderBottom: `1px solid ${theme.palette.grey.line}`,
              }}
            >
              {/* 제목 */}
              <Typography
                fontSize={{ xs: 16, md: 18 }}
                fontWeight={'bold'}
                sx={{ lineHeight: { xs: '1.18', md: '1.15' } }}
              >
                {notice.title}
              </Typography>

              {/* 날짜/댓글/북마크 + 버튼 */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  mt: { xs: '22px', md: '-6px' },
                }}
              >
                {/* 좌: 날짜, 댓글/북마크 */}
                <Box>
                  <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                    <Typography fontSize={14} color={theme.palette.black[4]}>
                      {notice.createdAt}
                    </Typography>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: '8px',
                        alignSelf: 'center',
                        backgroundColor: theme.palette.grey.line,
                      }}
                    />
                    <Box display="flex" gap={1.5} alignItems="center">
                      <CommentCount variant="detail" commentCount={notice.commentCount} />
                      <BookmarkCount variant="detail" bookmarkCount={notice.bookmarkCount} />
                    </Box>
                  </Box>
                </Box>

                {/* 우: 버튼 */}
                <Box display="flex" gap={1}>
                  <ShareButton
                    url={`/notice/${notice.id}`}
                    contentType={CONTENT_TYPE.NOTICE}
                    contentId={notice.id}
                    contentName={notice.title}
                    shareText={`${notice.title} — 포스트팝`}
                    pageType={PAGE_TYPE.NOTICE_POST}
                  />
                  <BookmarkButton isBookmarked={isBookmarked} onToggle={handleBookmarkToggle} />
                  {isAdmin && (
                    <>
                      <PinButton isPinned={isPinned} onToggle={handlePinToggle} />
                      <EditDeleteMenuButton onEdit={handleEditClick} onDelete={handleDelete} />
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            <Stack spacing={'30px'} sx={{ mt: { xs: '16px', md: '24px', lg: '30px' } }}>
              {/* 본문 */}
              <ContentViewer text={notice.content} />
              {/* 광고 */}
              <InFeedBoxAd pageType={PAGE_TYPE.NOTICE_POST} />
              {/* 댓글 */}
              <CommentSection
                commentData={comments}
                articleType="notice"
                articleId={notice.id}
                contentName={notice.title}
                onSubmit={handleCommentSubmit}
                onReply={handleCommentReply}
                onDelete={handleCommentDelete}
                commentScrollId={commentScrollId}
              />
            </Stack>
          </SidebarLayout>
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default NoticeDetail;
